import axiosInstance from '../axiosInstance';
import { initializeDB } from '../config/db';
import { syncConfig } from '../config/syncConfig';
import { DB_NAME } from '../config/constants';
import versionService from './versionService';

/**
 * Базовая функция синхронизации для большинства сущностей.
 * Если у элемента syncConfig нет кастомного syncHandler, используем дефолт:
 *   - clear + put все записи
 */
async function defaultSyncHandler(db, storeName, data) {
    // Проверяем существует ли таблица перед использованием
    if (!db.objectStoreNames.contains(storeName)) {
        console.error(`Table ${storeName} does not exist! Skipping sync.`);
        console.error('Available tables:', Array.from(db.objectStoreNames));
        return;
    }
    
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    await store.clear();
    for (const record of data) {
        await store.put(record);
    }
    await tx.done;
}

/**
 * @param {string} key   ключ из syncConfig («employees», «instructions», …)
 * @param {{silent?: boolean}} [options]  silent — не показывать глобальный тост
 *   прогресса. Для служебных фоновых рефрешей (например триггеры документов при
 *   открытии JHA): юзер такой синк не запрашивал и уведомление ему только мешает.
 */
export async function syncDataGeneric(key, options = {}) {
    const { silent = false } = options;
    const emitProgress = (detail) => {
        if (silent) return;
        window.dispatchEvent(new CustomEvent('syncEntityProgress', { detail }));
    };

    // key — это строка, соответствующая «employees», «instructions» и т.д. из syncConfig
    const configItem = syncConfig[key];
    if (!configItem) {
        console.error(`syncDataGeneric: нет конфигурации для ключа '${key}'`);
        return;
    }
    console.log(`[Sync] Starting sync for ${key}...`);

    // Диспетчим событие начала синхронизации конкретной сущности
    emitProgress({ key, phase: 'start' });

    try {
        // Форсируем переоткрытие БД при ручной синхронизации для гарантии свежего соединения
        // (внутри try: падение здесь тоже должно закрыть прогресс, а не оставить его висеть)
        const db = await initializeDB(true);

        // Проверяем есть ли lastSyncTime для инкрементальной синхронизации
        const lastSyncTime = localStorage.getItem(configItem.localStorageKey);
        let isFirstSync = !lastSyncTime;

        // Если syncHandler сам управляет загрузкой (пагинация и т.д.) — пропускаем дефолтный fetch
        if (configItem.skipDefaultFetch && typeof configItem.syncHandler === 'function') {
            console.log(`[Sync] Delegating fetch to custom handler for ${key}`);
            await configItem.syncHandler(db, configItem.storeName, null, isFirstSync);
            console.log(`Синхронизация '${key}' прошла успешно! (${isFirstSync ? 'full' : 'incremental'})`);
            // Кастомный handler сам сохраняет данные и timestamp, но событие
            // завершения обязано уйти и здесь — иначе тост «Syncing …» висит
            // на экране навсегда (он закрывается только по 'done'/'error').
            emitProgress({ key, phase: 'done' });
            return;
        }

        let endpoint = configItem.endpoint;

        // Если есть lastSyncTime и endpoint поддерживает инкрементальную синхронизацию
        if (lastSyncTime && configItem.supportsIncrementalSync) {
            const separator = endpoint.includes('?') ? '&' : '?';
            endpoint = `${endpoint}${separator}lastSyncTimestamp=${encodeURIComponent(lastSyncTime)}`;
            console.log(`[Sync] Incremental sync for ${key} from ${lastSyncTime}`);
        } else {
            console.log(`[Sync] Full sync for ${key} (first sync or no incremental support)`);
        }

        console.log(endpoint);
        const response = await axiosInstance.get(endpoint);

        // Обрабатываем ответ: поддерживаем и старый формат (массив) и новый (объект с data)
        let data = response.data;
        let serverTime = null;

        if (data && typeof data === 'object' && 'data' in data) {
            // Новый формат с data, serverTime, и т.д.
            serverTime = data.serverTime;
            data = data.data;
        }

        // Если есть кастомный handler, вызываем его, передаём флаг isFirstSync
        if (typeof configItem.syncHandler === 'function') {
            await configItem.syncHandler(db, configItem.storeName, data, isFirstSync);
        } else {
            await defaultSyncHandler(db, configItem.storeName, data);
        }

        // Записываем время (используем serverTime если есть, иначе клиентское)
        const syncTime = serverTime || new Date().toISOString();
        localStorage.setItem(configItem.localStorageKey, syncTime);
        console.log(`Синхронизация '${key}' прошла успешно! (${isFirstSync ? 'full' : 'incremental'})`);

        emitProgress({ key, phase: 'done' });
    } catch (error) {
        console.error(`Ошибка синхронизации '${key}':`, error);
        emitProgress({ key, phase: 'error', error: error.message });
    }
}

/**
 * Синхронизируем ВСЁ (по всем ключам из syncConfig).
 */
export async function syncAllData() {
    // Форсируем переоткрытие БД чтобы триггернуть upgrade если версия изменилась
    const db = await initializeDB(true);
    try {
        const keys = Object.keys(syncConfig);
        const total = keys.length;

        window.dispatchEvent(new CustomEvent('syncAllProgress', {
            detail: { phase: 'start', total, current: 0, currentKey: null }
        }));

        // Последовательная синхронизация:
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            window.dispatchEvent(new CustomEvent('syncAllProgress', {
                detail: { phase: 'progress', total, current: i, currentKey: key }
            }));
            await syncDataGeneric(key);
        }

        // Каталог кодов работ (Job code (day)) — тоже обновить при синке устройства:
        // без метки времени следующий pull таймшита попросит его у сервера заново.
        localStorage.removeItem('timesheetJobCodes:ts');

        // Записываем в localStorage «глобальное время»
        const currentTime = new Date().toISOString();
        localStorage.setItem('lastOverallSyncTime', currentTime);

        // Проверяем обновления приложения после синхронизации данных
        console.log('Checking for app updates after sync...');
        await versionService.checkForUpdate();

        window.dispatchEvent(new CustomEvent('syncAllProgress', {
            detail: { phase: 'done', total, current: total, currentKey: null }
        }));

        // Диспетчим кастомное событие
        const syncEvent = new CustomEvent('syncComplete', { detail: currentTime });
        window.dispatchEvent(syncEvent);
    } catch (error) {
        console.error('Ошибка синхронизации всех данных:', error);
        window.dispatchEvent(new CustomEvent('syncAllProgress', {
            detail: { phase: 'error', error: error.message }
        }));
    }
}

/**
 * Пример функции «clearAllData», оставляем как было, если надо чистить всю IndexedDB сразу.
 */
export const clearAllData = async () => {
    try {
        const dbNames = [DB_NAME];
        for (const dbName of dbNames) {
            await new Promise((resolve, reject) => {
                const request = indexedDB.deleteDatabase(dbName);
                request.onsuccess = () => {
                    console.log(`База данных ${dbName} успешно удалена`);
                    resolve();
                };
                request.onerror = (event) => {
                    console.error(`Ошибка при удалении базы данных ${dbName}:`, event.target.error);
                    reject(event.target.error);
                };
                request.onblocked = () => {
                    console.warn(`Удаление базы данных ${dbName} заблокировано`);
                };
            });
        }
        console.log('Все базы данных IndexedDB успешно удалены');
    } catch (error) {
        console.error('Ошибка при очистке данных из IndexedDB:', error);
    }
};
